/*
 * Product Navigation Menu - Auto-scroll functionality
 * Handles smooth scrolling to different sections of the product page
 */

(() => {
  function initProductNavigation() {
    const navMenu = document.getElementById('product-nav-menu') || document.getElementById('product-nav-menu-global');
    if (!navMenu) return;

    // Ensure the nav is rendered above all elements by moving it to <body>
    // This avoids being clipped by ancestor overflow or stacking contexts
    try {
      if (navMenu.parentElement && navMenu.parentElement.tagName !== 'BODY') {
        // Preserve computed width if needed (usually auto)
        const computed = window.getComputedStyle(navMenu);
        const width = computed.width;
        document.body.appendChild(navMenu);
        if (width && width !== 'auto') {
          navMenu.style.width = width;
        }
        // Reinforce fixed positioning and z-index
        navMenu.style.position = 'fixed';
        navMenu.style.zIndex = '2147483647';
        navMenu.style.pointerEvents = 'auto';
        navMenu.style.isolation = 'isolate';
      }
    } catch (e) {
      // no-op
    }

    // Set up footer detection to stop nav from overlaying footer
    setupFooterDetection(navMenu);
    
    // Set up dynamic positioning over film strip
    setupFilmStripPositioning(navMenu);

    // Get all navigation buttons
    const navButtons = navMenu.querySelectorAll('[data-scroll-to]');
    
    navButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        const targetId = button.getAttribute('data-scroll-to');
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          // Calculate offset from header if it exists
          const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
          const additionalOffset = 0; // No extra spacing - bring target to top of window
          const targetPosition = targetElement.offsetTop - headerHeight - additionalOffset;
          
          // Smooth scroll to target
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
          
          // Update active state
          updateActiveNavButton(button);
        }
      });
    });
    
    // Set up scroll spy to highlight current section
    setupScrollSpy(navButtons);
  }
  
  function updateActiveNavButton(activeButton) {
    const navMenu = document.getElementById('product-nav-menu') || document.getElementById('product-nav-menu-global');
    if (!navMenu) return;
    
    // Remove active state from all buttons
    navMenu.querySelectorAll('[data-scroll-to]').forEach(btn => {
      btn.classList.remove('active');
    });
    
    // Add active state to clicked button
    activeButton.classList.add('active');
  }
  
  // Sliding indicator functionality removed - active state shown via text color only
  
  function setupFilmStripPositioning(navMenu) {
    let isInitialPositioning = true;
    
    function updateNavPositioning(forceHide = false) {
      // Only hide nav if it's the initial positioning or explicitly forced
      if (isInitialPositioning || forceHide) {
        navMenu.classList.remove('positioned');
        // Ensure nav is off-screen during repositioning
        if (isInitialPositioning) {
          navMenu.style.left = '-9999px';
        }
      }
      
      // Find the product content area (right column) for positioning
      const productContent = document.querySelector('[id*="SecondColumn"], .product-content, [data-product-form]');
      if (!productContent) return;
      
      const contentRect = productContent.getBoundingClientRect();
      const contentWidth = contentRect.width;
      const contentLeft = productContent.offsetLeft;
      
      // Store current position to check if update is needed
      const currentLeft = parseFloat(navMenu.style.left) || -9999;
      
      // First, ensure nav has enough width to contain all buttons
      navMenu.style.width = 'max-content'; // Use max-content for proper sizing
      navMenu.style.whiteSpace = 'nowrap'; // Prevent wrapping
      
      // Check if nav content fits, adjust font size if needed
      adjustNavFontSize(navMenu, contentWidth);
      
      // Now position nav centered within product content width with screen edge protection
      const navWidth = navMenu.offsetWidth;
      const centeredLeft = contentLeft + (contentWidth - navWidth) / 2;
      
      // Ensure nav doesn't touch screen edges (minimal spacing)
      const minLeftMargin = 15; // Minimum 15px from left edge
      const maxRightPosition = window.innerWidth - navWidth - 15; // Minimum 15px from right edge
      const constrainedLeft = Math.max(minLeftMargin, Math.min(centeredLeft, maxRightPosition));
      
      // Only update position if it has changed significantly (more than 2px) or if initially positioning
      if (isInitialPositioning || Math.abs(currentLeft - constrainedLeft) > 2) {
        navMenu.style.left = constrainedLeft + 'px';
      }
      navMenu.style.transform = 'translateZ(9999px)'; // Keep 3D transform only
      
      // Show the nav menu after ensuring all layout calculations are complete
      if (isInitialPositioning || forceHide) {
        // Add extra delay on initial load to ensure everything is ready
        const delay = isInitialPositioning ? 100 : 0;
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              navMenu.classList.add('positioned');
              isInitialPositioning = false;
            });
          });
        }, delay);
      }
    }
    
    function adjustNavFontSize(navMenu, availableWidth) {
      const buttons = navMenu.querySelectorAll('button');
      let currentFontSize = 11; // Start with default 11px
      
      // Reset to default first
      buttons.forEach(btn => {
        btn.style.fontSize = '11px';
        const p = btn.querySelector('p');
        if (p) p.style.fontSize = '11px';
      });
      
      // Force layout recalculation
      navMenu.offsetWidth;
      
      // Check if content fits with minimal margin to prevent edge touching
      let navWidth = navMenu.scrollWidth;
      const maxWidth = availableWidth - 30; // Leave 30px margin (15px each side)
      
      if (navWidth > maxWidth) {
        // Reduce font size until it fits
        while (currentFontSize > 7 && navWidth > maxWidth) {
          currentFontSize -= 0.25; // Smaller increments for finer control
          buttons.forEach(btn => {
            btn.style.fontSize = currentFontSize + 'px';
            const p = btn.querySelector('p');
            if (p) p.style.fontSize = currentFontSize + 'px';
          });
          
          // Force layout recalculation and re-measure
          navMenu.offsetWidth;
          navWidth = navMenu.scrollWidth;
        }
      }
    }
    
    // Update on load and resize (with debounce)
    updateNavPositioning();
    
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => updateNavPositioning(true), 200); // Force hide only on resize
    });
    
    // Re-run when content changes (with debounce) - but be selective
    let mutationTimeout;
    let mutationCount = 0;
    const observer = new MutationObserver((mutations) => {
      // Ignore mutations that don't affect layout
      const relevantMutation = mutations.some(mutation => {
        // Check if mutation affects the film strip or main content areas
        const target = mutation.target;
        if (target.nodeType !== Node.ELEMENT_NODE) return false;
        
        // Ignore style changes to the nav itself
        if (target === navMenu || navMenu.contains(target)) return false;
        
        // Ignore unrelated areas
        if (target.closest('.header, .footer, .announcement-bar')) return false;
        
        // Check for relevant changes, but ignore metafield gallery
        if (target.closest('.product-metafield-gallery, [class*="metafield-gallery"]')) return false;
        return target.closest('[id*="ThirdColumn"], .third-column, .product-content, main');
      });
      
      if (!relevantMutation) return;
      
      clearTimeout(mutationTimeout);
      mutationCount++;
      // Only force hide after multiple mutations (likely a major change)
      const shouldForceHide = mutationCount > 5;
      mutationTimeout = setTimeout(() => {
        updateNavPositioning(shouldForceHide);
        mutationCount = 0; // Reset counter
      }, 100);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
  }

  function setupFooterDetection(navMenu) {
    let ticking = false;
    
    function checkFooterIntersection() {
      const footer = document.querySelector('footer, .footer, [class*="footer"]');
      if (!footer) {
        ticking = false;
        return;
      }
      
      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const navHeight = navMenu.offsetHeight || 60; // fallback height
      
      // Only switch when footer is ACTUALLY visible (more conservative threshold)
      // Give 100px buffer before switching to absolute positioning
      if (footerRect.top <= windowHeight - 100) {
        // Debug log to see when this triggers
        console.log('Product nav: Switching to absolute positioning for footer avoidance', {
          footerTop: footerRect.top,
          windowHeight: windowHeight,
          threshold: windowHeight - 100
        });
        
        // Switch to absolute positioning and position above footer
        const footerTop = footer.offsetTop;
        navMenu.style.position = 'absolute';
        navMenu.style.top = (footerTop - navHeight - 20) + 'px'; // 20px gap above footer
        navMenu.style.bottom = 'auto';
        // Keep original horizontal positioning (don't override CSS left/transform)
        // The CSS already handles centering over the film strip column
      } else {
        // Back to fixed positioning
        navMenu.style.position = 'fixed';
        navMenu.style.top = 'auto';
        navMenu.style.bottom = '20px';
        // CSS handles the original positioning
      }
      
      ticking = false;
    }
    
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(checkFooterIntersection);
        ticking = true;
      }
    }
    
    // Add scroll listener for footer detection
    window.addEventListener('scroll', onScroll, { passive: true });
    
    // Initial check
    checkFooterIntersection();
  }
  
  function setupScrollSpy(navButtons) {
    const navMenu = document.getElementById('product-nav-menu') || document.getElementById('product-nav-menu-global');
    
    // Throttle scroll events for performance
    let ticking = false;
    
    function updateScrollSpy() {
      const scrollPosition = window.scrollY + 200; // Fixed offset for better section detection
      
      let activeSection = null;
      
      navButtons.forEach(button => {
        const targetId = button.getAttribute('data-scroll-to');
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          const elementTop = targetElement.offsetTop;
          const elementBottom = elementTop + targetElement.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            activeSection = button;
          }
        }
      });
      
      if (activeSection) {
        updateActiveNavButton(activeSection);
      }
      
      ticking = false;
    }
    
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateScrollSpy);
        ticking = true;
      }
    }
    
    // Add scroll listener
    window.addEventListener('scroll', onScroll, { passive: true });
    
    // Set initial active state
    updateScrollSpy();
    
    // Initialize active state (no sliding indicator needed)
    setTimeout(() => {
      const activeBtn = navMenu.querySelector('button.active');
      if (!activeBtn) {
        // Default to first button if no active state
        const firstBtn = navButtons[0];
        if (firstBtn) {
          updateActiveNavButton(firstBtn);
        }
      }
    }, 100);
  }


  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductNavigation);
  } else {
    initProductNavigation();
  }
  
  // Re-initialize for dynamic content (AJAX, etc.)
  document.addEventListener('shopify:section:load', initProductNavigation);
})();
